import { useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import type { DSVariable } from '@/api/deep-signal-api';
import { variableDisplay } from '../builders';

export function WhatWeTestedPanel({ brand, variables, onAskAI }: { brand: string; variables: DSVariable[]; onAskAI?: (q: string) => void }) {
  const [open, setOpen] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [methodologyOpen, setMethodologyOpen] = useState(false);
  const [methodologyHovered, setMethodologyHovered] = useState(false);

  const rows = variables.map((v, i) => {
    const { name, def } = variableDisplay(v);
    return { num: i + 1, id: v.id, name, def };
  });

  return (
    <div style={{
      borderBottom: '1px solid var(--border)',
      backgroundColor: 'var(--background)',
    }}>
      {/* Toggle header */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 64px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          borderBottom: open ? '1px solid var(--border)' : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
            <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, letterSpacing: '0.5px', color: 'var(--muted-foreground)', marginBottom: '6px' }}>
              What We Tested
            </span>
            <span style={{ fontSize: 'var(--text-2xl)', fontWeight: 600, color: 'var(--foreground)' }}>
              The {rows.length} brand attributes we want AI models to understand about {brand}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>
            {open ? 'Collapse' : 'Expand'}
          </span>
          <span style={{
            display: 'inline-block',
            fontSize: 'var(--text-base)',
            color: 'var(--muted-foreground)',
            lineHeight: 1,
            transition: 'transform 0.2s',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}>
            ⌄
          </span>
        </div>
      </button>

      {/* Expandable body */}
      {open && (
        <div style={{ padding: '20px 64px 24px' }}>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)', lineHeight: '1.55', marginBottom: '18px' }}>
            These are the specific, verifiable claims about this brand that we tested across the AI models measured in this run. A variable encodes when a model can accurately describe it in response to a buyer query.
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '10px',
          }}>
            {rows.map((v) => {
              const isExpanded = expandedId === v.id;
              const isHovered = hoveredId === v.id;
              return (
                <div
                  key={v.id}
                  onClick={() => v.def && setExpandedId(prev => prev === v.id ? null : v.id)}
                  onMouseEnter={() => setHoveredId(v.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    border: `1px solid ${isHovered && v.def ? '#00c9c4' : 'var(--border)'}`,
                    borderRadius: '8px',
                    backgroundColor: 'var(--background)',
                    padding: '14px 16px',
                    cursor: v.def ? 'pointer' : 'default',
                    boxShadow: isHovered && v.def ? '0 0 0 3px rgba(0,201,196,0.12)' : 'none',
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                  }}
                >
                  {/* Number badge */}
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '12px',
                    backgroundColor: '#e1fdfd',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: '1px',
                  }}>
                    <span style={{ fontSize: 'var(--text-xs)', fontWeight: 700, color: '#00c9c4' }}>
                      {v.num}
                    </span>
                  </div>
                  {/* Text */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                      <p style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--foreground)', marginBottom: '4px' }}>
                        {v.name}
                      </p>
                      {v.def && (
                        <span style={{
                          display: 'inline-block',
                          fontSize: 'var(--text-base)',
                          color: 'var(--muted-foreground)',
                          lineHeight: 1,
                          flexShrink: 0,
                          transition: 'transform 0.2s',
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}>
                          ⌄
                        </span>
                      )}
                    </div>
                    {v.def && (
                      <p style={{
                        fontSize: 'var(--text-xs)',
                        color: 'var(--muted-foreground)',
                        lineHeight: '1.5',
                        display: isExpanded ? 'block' : '-webkit-box',
                        WebkitLineClamp: isExpanded ? undefined : 2,
                        WebkitBoxOrient: isExpanded ? undefined : 'vertical',
                        overflow: isExpanded ? 'visible' : 'hidden',
                      }}>
                        {v.def}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Methodology tile — fills out the grid, explains how scoring works */}
            <div
              onClick={() => setMethodologyOpen(true)}
              onMouseEnter={() => setMethodologyHovered(true)}
              onMouseLeave={() => setMethodologyHovered(false)}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                border: `1px dashed ${methodologyHovered ? '#7e5fe0' : 'var(--border)'}`,
                borderRadius: '8px',
                backgroundColor: methodologyHovered ? '#f4f1fb' : 'var(--background)',
                padding: '14px 16px',
                cursor: 'pointer',
                transition: 'border-color 0.15s, background-color 0.15s',
              }}
            >
              <div style={{
                width: '24px',
                height: '24px',
                borderRadius: '12px',
                backgroundColor: '#ece7fa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                marginTop: '1px',
              }}>
                <span style={{ fontSize: 'var(--text-xs)', fontWeight: 700, color: '#7e5fe0' }}>?</span>
              </div>
              <div>
                <p style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: '#7e5fe0', marginBottom: '4px' }}>
                  How do we score these variables in model responses?
                </p>
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', lineHeight: '1.5' }}>
                  See our scoring methodology.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Methodology modal */}
      {methodologyOpen && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.45)' }}
          onClick={() => setMethodologyOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--card)',
              border: '1px solid var(--border)',
              borderRadius: '12px',
              padding: '32px',
              maxWidth: '520px',
              width: '90%',
              maxHeight: '80vh',
              overflowY: 'auto',
              position: 'relative',
              boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
            }}
          >
            {/* Close */}
            <button
              onClick={() => setMethodologyOpen(false)}
              style={{ position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted-foreground)', padding: '4px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--muted)'; e.currentTarget.style.color = 'var(--foreground)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--muted-foreground)'; }}
            >
              <X size={18} />
            </button>

            {/* Title */}
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 700, color: 'var(--foreground)', marginBottom: '6px' }}>How we score model responses</h3>
              <p style={{ fontSize: 'var(--text-base)', color: 'var(--muted-foreground)', lineHeight: '1.55' }}>
                An LLM scorer model reads each response against a rubric written for that specific brand attribute, with separate criteria for a score of 2 (clearly and accurately addressed), 1 (partially or vaguely addressed), or 0 (not addressed at all). This runs across every query, model, and persona in the experiment.
              </p>
            </div>

            {/* Human verification */}
            <div style={{ marginBottom: '18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <div style={{ width: '4px', height: '16px', borderRadius: '99px', backgroundColor: '#28a9d2', flexShrink: 0 }} />
                <h4 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--foreground)' }}>Human verification</h4>
              </div>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)', lineHeight: '1.6' }}>
                We pull a sample of scored responses and have a human reviewer independently score that same sample against the identical rubric, blind to the model's scores.
              </p>
            </div>

            {/* What is a Kappa score and why does it matter */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <div style={{ width: '4px', height: '16px', borderRadius: '99px', backgroundColor: '#00c9c4', flexShrink: 0 }} />
                <h4 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--foreground)' }}>What is a Kappa score and why does it matter?</h4>
              </div>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)', lineHeight: '1.6' }}>
                Kappa measures how closely the human reviewer's scores match the model scorer's scores, weighted so that a 0-vs-2 disagreement counts as worse than a 0-vs-1 disagreement. It matters because it's the check on whether you can trust the scorer model's judgment: a high Kappa means the automated scores are standing in for a human's, a low one means the scores need a closer look before you act on them.
              </p>
            </div>

            {/* CTA */}
            <button
              onClick={() => {
                onAskAI?.('How is the scoring methodology applied to our brand attributes?');
                setMethodologyOpen(false);
              }}
              style={{
                width: '100%',
                padding: '12px 20px',
                backgroundColor: '#7e5fe0',
                border: 'none',
                borderRadius: '8px',
                fontSize: 'var(--text-sm)',
                fontWeight: 600,
                color: '#fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
            >
              <Sparkles size={15} />
              Continue this conversation with AI &#8594;
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
